import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { formatTime12h, formatDateMonthDay, formatFullDateTime12h } from '../../utils/timeFormat';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import {
  Home, Bell, Briefcase, Calendar, Users, Search, Image,
  ChevronRight, Plus, Loader2, MapPin, Clock, BarChart3,
  User, Heart, MessageCircle, Camera, Building2, Award, Lock, LogOut
} from 'lucide-react';
import { toast } from 'sonner';
import { FeedReactions } from '../../components/FeedReactions';
import { FeedPostContent } from '../../components/FeedPostContent';
import PendingRatingsWidget from '../../components/PendingRatingsWidget';
import WelcomeVideo from '../../components/WelcomeVideo';
import { HelpButton } from '../../components/HelpCenter';
import HelpMenu from '../../components/HelpMenu';
import EnablePushBanner from '../../components/EnablePushBanner';
import SmartSuggestionsWidget from '../../components/SmartSuggestionsWidget';
import BillingOverview from '../../components/BillingOverview';
import UpgradeToProCard from '../../components/UpgradeToProCard';
import OpenShiftsPanel from '../../components/OpenShiftsPanel';
import TopTalentForShifts from '../../components/TopTalentForShifts';
import QuickActionBar from '../../components/QuickActionBar';
// Iter-250 — Community adoption widgets
import CommunityHighlights from '../../components/CommunityHighlights';
import CommunityIntroModal, { useCommunityIntroFlag } from '../../components/CommunityIntroModal';
import { useNoOtherDialogOpen } from '../../hooks/useNoOtherDialogOpen';

// Feed Post Component (simplified for client view)
const FeedPost = ({ post, onLike, onReactionUpdate }) => {
  return (
    <Card className="mb-4" data-testid="feed-post">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {post.author_photo ? (
            <img src={post.author_photo} alt="" className="w-10 h-10 rounded-full object-cover" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center">
              <User className="w-5 h-5 text-cyan-600" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-900">{post.author_name}</span>
              {post.post_type === 'achievement' && (
                <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs">
                  <Award className="w-3 h-3 mr-1" />Achievement
                </Badge>
              )}
              {post.post_type === 'event_recap' && (
                <Badge className="bg-cyan-100 text-cyan-700 border-cyan-200 text-xs">
                  <Camera className="w-3 h-3 mr-1" />Event Recap
                </Badge>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              {formatFullDateTime12h(post.created_at)}
            </p>
            <FeedPostContent content={post.content} className="text-gray-700 mt-2 whitespace-pre-wrap block" />
            
            {/* Media */}
            {post.media_urls && post.media_urls.length > 0 && typeof post.media_urls !== 'string' && (
              <div className="grid grid-cols-2 gap-2 mt-3">
                {post.media_urls.map((url, i) => (
                  <img key={i} src={url} alt="" className="rounded-lg w-full h-40 object-cover" />
                ))}
              </div>
            )}
            
            {/* Actions */}
            <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100">
              <FeedReactions postId={post.id} reactions={post.reactions} onUpdate={onReactionUpdate} />
              <span className="flex items-center gap-1.5 text-sm text-gray-500">
                <MessageCircle className="w-4 h-4" />
                {post.comments_count || 0}
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const ClientHome = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const handleLogout = async () => {
    try { await logout(); } catch (e) { /* ignore */ }
    navigate('/login');
  };
  const [loading, setLoading] = useState(true);
  const [homeData, setHomeData] = useState(null);
  const [feedPosts, setFeedPosts] = useState([]);
  const [feedLoading, setFeedLoading] = useState(false);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [newPostContent, setNewPostContent] = useState('');
  const [creatingPost, setCreatingPost] = useState(false);

  useEffect(() => {
    fetchHomeData();
    fetchFeed();
    checkSubscription();
  }, []);

  const checkSubscription = async () => {
    try {
      const res = await api.get('/monetization/client-subscriptions/status');
      setHasActiveSubscription(res.data?.has_subscription === true);
    } catch (e) {
      setHasActiveSubscription(false);
    }
  };

  const handleCreatePost = async () => {
    if (!newPostContent.trim()) {
      toast.error('Please enter some content');
      return;
    }
    setCreatingPost(true);
    try {
      await api.post('/feed', {
        post_type: 'text',
        content: newPostContent.trim()
      });
      toast.success('Post created successfully!');
      setShowCreatePost(false);
      setNewPostContent('');
      fetchFeed();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.includes('Upgrade your plan')) {
        toast.error(detail);
      } else {
        toast.error('Failed to create post');
      }
    } finally {
      setCreatingPost(false);
    }
  };

  const fetchHomeData = async () => {
    try {
      const res = await api.get('/home');
      setHomeData(res.data);
    } catch (e) {
      toast.error('Failed to load home data');
    } finally {
      setLoading(false);
    }
  };

  const fetchFeed = async () => {
    setFeedLoading(true);
    try {
      const res = await api.get('/feed');
      setFeedPosts(res.data.posts || []);
    } catch (e) {
      console.error('Failed to load feed:', e);
    } finally {
      setFeedLoading(false);
    }
  };

  const handleLike = async (postId) => {
    try {
      const res = await api.post(`/feed/${postId}/like`);
      setFeedPosts(posts => posts.map(p => 
        p.id === postId 
          ? { ...p, user_liked: res.data.liked, likes_count: p.likes_count + (res.data.liked ? 1 : -1) }
          : p
      ));
    } catch (e) {
      toast.error('Failed to like post');
    }
  };

  const handleReactionUpdate = (postId, newReactions) => {
    setFeedPosts(posts => posts.map(p =>
      p.id === postId ? { ...p, reactions: newReactions } : p
    ));
  };

  // Iter-292g — Community intro modal (see CommunityIntroModal.js).
  const { seen: introSeen } = useCommunityIntroFlag();
  const [showCommunityIntro, setShowCommunityIntro] = useState(false);
  const safeToOpenCommunity = useNoOtherDialogOpen(!introSeen && !showCommunityIntro, 'community-intro-modal');
  useEffect(() => {
    if (introSeen) {
      setShowCommunityIntro(false);
      return;
    }
    if (!safeToOpenCommunity) return;
    // Don't open over a mandatory Service Agreement gate.
    if (window.__agreementGateBlocking === true) return;
    const t = setTimeout(() => {
      if (window.__agreementGateBlocking === true) return;
      setShowCommunityIntro(true);
    }, 2500);
    return () => clearTimeout(t);
  }, [introSeen, safeToOpenCommunity]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#03DFFC]" />
      </div>
    );
  }

  const { profile, open_gigs, upcoming_gigs, recent_media, analytics_snapshot, tagged_posts_count, notifications_count } = homeData || {};

  return (
    <div className="min-h-screen bg-gray-50" data-testid="client-home">
      <CommunityIntroModal open={showCommunityIntro} onClose={() => setShowCommunityIntro(false)} />
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <button onClick={() => navigate('/client/home')} className="flex items-center gap-2 hover:opacity-80 transition-opacity shrink-0" data-testid="home-link">
              <Home className="w-5 h-5 text-[#03DFFC]" />
              <span className="text-sm font-bold text-[#03DFFC] hidden sm:inline">iEvntur</span>
            </button>
            <h1 className="text-base sm:text-lg font-semibold text-gray-900 truncate" data-testid="client-greeting">{(() => { const h = new Date().getHours(); const name = homeData?.profile?.contact_name?.split(' ')[0] || ''; return h < 12 ? `Good morning${name ? ', ' + name : ''}` : h < 17 ? `Good afternoon${name ? ', ' + name : ''}` : `Good evening${name ? ', ' + name : ''}`; })()}</h1>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <HelpMenu />
            <Button variant="ghost" size="sm" onClick={() => navigate('/client/notifications')} className="relative px-2">
              <Bell className="w-5 h-5" />
              {notifications_count > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-gray-900 text-[10px] rounded-full flex items-center justify-center">
                  {notifications_count}
                </span>
              )}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate('/client/dashboard')} className="px-2" title="Profile">
              <Building2 className="w-5 h-5" />
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="px-2 text-gray-600 hover:text-red-700" data-testid="client-home-logout-btn" title="Logout">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Left Column - Profile & Quick Actions */}
          <div className="space-y-4">
            {/* Company Card */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-4">
                  {profile?.logo ? (
                    <img src={profile.logo} alt="" className="w-12 h-12 rounded-lg object-cover" />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-cyan-100 flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-cyan-600" />
                    </div>
                  )}
                  <div>
                    <h2 className="font-semibold text-gray-900">{profile?.company_name || 'Company'}</h2>
                    <p className="text-sm text-gray-500">Client Account</p>
                  </div>
                </div>
                <Button 
                  className="w-full bg-[#03DFFC] hover:bg-[#03DFFC]"
                  onClick={() => navigate('/client/shifts/create')}
                  data-testid="post-gig-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Post a Gig
                </Button>
                <Button
                  variant="outline"
                  className="w-full mt-2 border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                  onClick={() => navigate('/client/invoices')}
                  data-testid="view-invoices-btn"
                >
                  View Invoices
                </Button>
                <Button
                  variant="outline"
                  className="w-full mt-2 border-slate-800 text-slate-900 hover:bg-slate-50"
                  onClick={() => navigate('/client/insights')}
                  data-testid="client-insights-btn"
                >
                  Campaign Intelligence
                </Button>
              </CardContent>
            </Card>

            {/* Welcome Video Card — Iter-337: no hardcoded videoUrl.
                Admin controls the URL via /admin/video-settings; this
                component fetches it via settingsKey. Passing videoUrl
                here would override the admin setting and lead to the
                exact bug fixed in Iter-337 (Rick Astley leak). */}
            <WelcomeVideo 
              settingsKey="client_welcome_video_url"
              title="Welcome to iEvntür"
              subtitle="Watch this quick walkthrough to get started"
              showOnFirstVisit={true}
            />

            {/* Quick Actions — Stacked vertical list with drag-and-drop reorder */}
            <QuickActionBar role="client" />
            {/* Iter-250 — Community Highlights ribbon */}
            <CommunityHighlights />

            {/* Conversion nudge — hides itself for Pro/Enterprise users and after dismissal */}
            <UpgradeToProCard />

            {/* Billing at a glance — shows client_type, terms, approval, next due */}
            <BillingOverview />

            {/* Analytics Snapshot */}
            <Card>
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <BarChart3 className="w-4 h-4" />
                  Analytics Snapshot
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-cyan-600">{analytics_snapshot?.total_events || 0}</p>
                    <p className="text-xs text-gray-500">Total Events</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">{analytics_snapshot?.completed_events || 0}</p>
                    <p className="text-xs text-gray-500">Completed</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-amber-600">{analytics_snapshot?.total_talent_booked || 0}</p>
                    <p className="text-xs text-gray-500">Talent Booked</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-purple-600">{Math.round(analytics_snapshot?.total_hours || 0)}</p>
                    <p className="text-xs text-gray-500">Hours Worked</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Center Column - Feed & Events */}
          <div className="lg:col-span-2 space-y-4">
            {/* Iter-245 — Push enrollment banner. Hides automatically
                once the user enables push or dismisses for 30 days. */}
            <EnablePushBanner />
            {/* Pending Ratings Widget */}
            <PendingRatingsWidget userRole="client" />
            
            {/* Smart Suggestions - Recommended Talent */}
            <SmartSuggestionsWidget maxItems={4} />
            
            {/* Open Gigs */}
            {open_gigs?.length > 0 && (
              <Card className="border-cyan-200 bg-cyan-50">
                <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
                  <CardTitle className="text-sm font-medium text-cyan-800 flex items-center gap-2">
                    <Briefcase className="w-4 h-4" />
                    Open Gigs
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/client/shifts')}>
                    View All <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  <div className="space-y-2">
                    {open_gigs.slice(0, 3).map(gig => (
                      <div key={gig.gig_id} className="flex items-center justify-between p-3 bg-white rounded-lg">
                        <div>
                          <p className="font-medium text-gray-900">{gig.venue_name}</p>
                          <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDateMonthDay(gig.shift_date)}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {gig.city}, {gig.state}
                            </span>
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {gig.booked_count}/{gig.talent_needed} booked
                            </span>
                          </div>
                        </div>
                        <Badge variant="outline">{gig.role_name}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Upcoming Gigs */}
            {upcoming_gigs?.length > 0 && (
              <Card>
                <CardHeader className="py-3 px-4">
                  <CardTitle className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Upcoming Events (Next 2 Weeks)
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  <div className="space-y-2">
                    {upcoming_gigs.slice(0, 4).map(gig => (
                      <div key={gig.gig_id} className="flex items-center justify-between p-3 border border-gray-100 rounded-lg">
                        <div>
                          <p className="font-medium text-gray-900">{gig.venue_name}</p>
                          <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDateMonthDay(gig.shift_date)} at {formatTime12h(gig.start_time)}
                            </span>
                            <span className="text-green-600 font-medium">
                              {gig.staffed_count ?? gig.confirmed_count ?? 0} staffed
                            </span>
                          </div>
                        </div>
                        <Badge className="bg-cyan-100 text-cyan-700">{gig.role_name}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Recent Media Uploads */}
            {recent_media?.length > 0 && (
              <Card>
                <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
                  <CardTitle className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Image className="w-4 h-4" />
                    Recent Event Media
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/client/events/analytics')}>
                    View All <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  <div className="grid grid-cols-3 gap-2">
                    {recent_media.slice(0, 6).map(media => (
                      <div key={media.id} className="relative group">
                        <img 
                          src={media.media_url} 
                          alt={media.caption || ''} 
                          className="w-full h-24 object-cover rounded-lg"
                        />
                        <div className="absolute inset-0 bg-gray-50/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-end p-2">
                          <p className="text-gray-900 text-xs truncate">{media.venue_name}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Community Feed / Tagged Posts */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <MessageCircle className="w-4 h-4" />
                  Community Feed
                  {tagged_posts_count > 0 && (
                    <Badge variant="outline" className="text-xs">{tagged_posts_count} posts mention you</Badge>
                  )}
                </h3>
                
                {/* Create Post Button - Subscription Required */}
                {hasActiveSubscription ? (
                  <Button
                    size="sm"
                    className="bg-cyan-500 hover:bg-cyan-600"
                    onClick={() => setShowCreatePost(true)}
                    data-testid="create-post-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Post
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-gray-400 border-gray-300"
                    onClick={() => {
                      toast.info('Upgrade your plan to post on the community board', {
                        action: {
                          label: 'Upgrade',
                          onClick: () => navigate('/client/settings/subscription')
                        }
                      });
                    }}
                    data-testid="locked-post-btn"
                  >
                    <Lock className="w-4 h-4 mr-1" />
                    Post
                  </Button>
                )}
              </div>
              
              {feedLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-[#03DFFC]" />
                </div>
              ) : feedPosts.length === 0 ? (
                <Card>
                  <CardContent className="py-8 text-center">
                    <MessageCircle className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-500">No posts yet</p>
                    <p className="text-sm text-gray-500 mt-1">Talent posts from your events will appear here</p>
                  </CardContent>
                </Card>
              ) : (
                feedPosts.map(post => (
                  <FeedPost key={post.id} post={post} onLike={handleLike} onReactionUpdate={handleReactionUpdate} />
                ))
              )}
            </div>
          </div>
          
          {/* Right Column - Action Dashboard */}
          <div className="space-y-4 hidden lg:block">
            {/* Shifts Needing Attention */}
            <OpenShiftsPanel maxShifts={3} />
            
            {/* Top Talent Recommendations */}
            <TopTalentForShifts maxItems={3} />
          </div>
        </div>
      </div>
      
      {/* Create Post Dialog */}
      <Dialog open={showCreatePost} onOpenChange={setShowCreatePost}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle>Create Post</DialogTitle>
          <DialogDescription className="sr-only">Create Post</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              placeholder="Share something with the community..."
              value={newPostContent}
              onChange={(e) => setNewPostContent(e.target.value)}
              rows={4}
              className="resize-none"
              data-testid="post-content-input"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setShowCreatePost(false); setNewPostContent(''); }}>
                Cancel
              </Button>
              <Button 
                className="bg-cyan-500 hover:bg-cyan-600"
                onClick={handleCreatePost}
                disabled={creatingPost || !newPostContent.trim()}
                data-testid="submit-post-btn"
              >
                {creatingPost ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Post
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Help Center Button */}
      <HelpButton userType="client" />
    </div>
  );
};

export default ClientHome;
